// vim: set ts=2 sts=2 sw=2 et:

import React, { useState } from "react";
import { connect } from "react-redux";
import Button from "react-bootstrap/Button";

import type { GlobalState } from "../../types/stateTypes";
import { buildWebUploadBundle } from "./buildExportBundle";
import { getDeviceId } from "./deviceIdentity";
import { fetchDeviceSnapshotOrNull, pushMeetSnapshot } from "./syncClient";
import { describeSummary, overwriteWarning, summarizeMeet, syncUrlFrom, webBackupBlocker } from "./webBackup";

import styles from "./WebBackup.module.scss";

type Props = { global: GlobalState };

type Status = { text: string; error: boolean };

const timeLabel = (date: Date): string => date.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });

/**
 * "Respaldar en la web" (header): sube el torneo completo de esta PC. Solo con el torneo cargado y
 * abierto desde la web. No toca el ranking: eso sigue siendo "Enviar resultados a la web".
 */
const WebBackupButtonInner = ({ global }: Props) => {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);

  // Estados parciales (tests del header, estados muy viejos) no tienen sesión ni inscripción.
  if (!global.wrpfSession || !global.registration) return null;

  const blocker = webBackupBlocker(global);
  const session = global.wrpfSession;
  const syncUrl = syncUrlFrom(session);

  const handleClick = async () => {
    if (blocker !== null || syncUrl === null || !session.callbackToken) return;
    setBusy(true);
    setStatus(null);
    try {
      const deviceId = getDeviceId();
      const local = summarizeMeet(global);
      const previous = await fetchDeviceSnapshotOrNull(syncUrl, session.callbackToken, deviceId);
      const remote = previous?.registration?.entries ? summarizeMeet(previous as unknown as GlobalState) : null;
      const warning = overwriteWarning(local, remote);
      if (warning !== null && !window.confirm(warning)) return;

      // Sin etiqueta (la web usa la de la sesión) y sin tarimas propias: un respaldo no reclama ninguna
      // tarima en la fusión de resultados.
      await pushMeetSnapshot(syncUrl, session.callbackToken, {
        deviceId,
        ownedPlatforms: [],
        bundle: buildWebUploadBundle(global),
      });
      setStatus({ text: `Respaldado ${timeLabel(new Date())}: ${describeSummary(local)}`, error: false });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.headerBackup}>
      {status !== null ? (
        <span role="status" className={status.error ? styles.headerError : styles.headerStatus}>
          {status.text}
        </span>
      ) : null}
      <Button
        size="sm"
        className={styles.whiteButton}
        onClick={handleClick}
        disabled={busy || blocker !== null}
        title={blocker ?? "Sube a la web una copia completa del torneo de esta PC"}
      >
        {busy ? "Respaldando…" : "Respaldar en la web"}
      </Button>
    </div>
  );
};

const mapState = (state: GlobalState): Props => ({ global: state });

export const WebBackupButton = connect(mapState)(WebBackupButtonInner);

export default WebBackupButton;
